/// <amd-module name="Repos/Views/PullRequestListingPage/Components/PullRequestListingPage" />
import * as React from "react";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { VersionDropdownFilterBarItem } from "Repos/VersionDropdown/Components/VersionDropdownFilterBarItem";
import * as Resources from "Repos/Views/PullRequestListingPage/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { HistoryBehavior, IPageHistoryObjectFilterField, IPageStateHistoryService } from "VSS/Features/History";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IdentityPickerDropdownFilterBarItem, IIdentity, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Tab, TabBar } from "VSSUI/Tabs";
import { TextField, TextFieldFocusTreatmentBehavior } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { Filter } from "VSSUI/Utilities/Filter";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { IPullRequestListingService } from "../PullRequestListingService";
import { PullRequestListingPageTab } from "./PullRequestListingPageTab";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

class PullRequestListingPage extends VssComponent<{}> {
    private commandBarItems: IHeaderCommandBarItem[] = [];
    private commandBarRef = React.createRef<HeaderCommandBarWithFilter>();
    private filter = new Filter();
    private filterVisible = new ObservableValue<boolean | undefined>(undefined);
    private initialAssignedTo: ObservableValue<IIdentity>;
    private initialAssignedToText: ObservableValue<string>;
    private initialCreatedBy: ObservableValue<IIdentity>;
    private initialCreatedByText: ObservableValue<string>;
    private isMember: boolean;
    private isMaterializePullRequestIdentitiesFFEnabled: boolean;
    private pickerProvider: IPeoplePickerProvider;
    private pullRequestId = new ObservableValue("");
    private selectedTabId: ObservableValue<string>;
    private tabUrls: { [tabId: string]: string } = {};

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.isMember = userHasClaim(this.context.pageContext, UserClaims.Member);

        const reposRoutingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");

        this.pickerProvider = new PeoplePickerProvider(
            context.pageContext,
            {
                options: {
                    MinResults: 5,
                    MaxResults: 20
                }
            },
            this.filterIdentities
        );

        // Build urls for tabs, including the _a query param - allows for Open in New Tab behavior
        this.tabUrls["mine"] = reposRoutingService.getPullRequestsUrl("mine");
        this.tabUrls["active"] = reposRoutingService.getPullRequestsUrl("active");
        this.tabUrls["completed"] = reposRoutingService.getPullRequestsUrl("completed");
        this.tabUrls["abandoned"] = reposRoutingService.getPullRequestsUrl("abandoned");

        // Read the _a query param to set the initially selected tab - defaults to mine
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        this.selectedTabId = new ObservableValue(historyState["_a"] || (this.isMember ? "mine" : "active"));

        // Update the _a query param when the selected tab changes
        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageHistoryService.registerObservable(this.selectedTabId, "_a", HistoryBehavior.replace);

        const assignedToFilterField = this.createFilterHistoryFieldForIdentity("assignedTo");
        const createdByFilterField = this.createFilterHistoryFieldForIdentity("createdBy");
        const targetRefField: IPageHistoryObjectFilterField<string> = {
            dataType: "object",
            filterKey: "targetRefName",
            historyBehavior: HistoryBehavior.replace,
            mappingFunction: data => (data ? branchNameToRef(data) : ((null as any) as string)),
            parsingFunction: navValue => navValue
        };
        pageHistoryService.registerFilter(this.filter, "", [assignedToFilterField, createdByFilterField, targetRefField]);

        const canCreatePR = this.context.pageContext
            .getService<IReposPermissionsService>("IReposPermissionsService")
            .hasPermission(GitRepositoryPermissions.PullRequestContribute);

        if (canCreatePR) {
            this.commandBarItems.push({
                href: reposRoutingService.getPullRequestCreateUrl(),
                id: "create-pr",
                isPrimary: true,
                onActivate: this.onActivateCreatePullRequest,
                text: Resources.CreatePullRequest
            });
        }

        if (!this.isMember) {
            this.filter.reset();
        }

        if (this.filter.hasChangesToReset() || this.selectedTabId.value !== "mine") {
            this.filterVisible.value = true;
        }
        this.isMaterializePullRequestIdentitiesFFEnabled = isFeatureFlagEnabled(context.pageContext, "Repos.Policy.MaterializePullRequestIdentities");
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow custom-scrollbar scroll-auto-hide" selectedTabId={this.selectedTabId}>
                    <ScreenSizeObserver>
                        {({ screenSize }: { screenSize: ScreenSize }) => {
                            return (
                                <Observer selectedTabId={this.selectedTabId}>
                                    {({ selectedTabId }: { selectedTabId: string }) => {
                                        return (
                                            <Header
                                                commandBarItems={this.getCommandBarItems(selectedTabId, screenSize)}
                                                title={Resources.PullRequests}
                                                titleSize={TitleSize.Large}
                                            />
                                        );
                                    }}
                                </Observer>
                            );
                        }}
                    </ScreenSizeObserver>
                    <TabBar
                        onSelectedTabChanged={this.onSelectedTabChanged}
                        renderAdditionalContent={this.isMember ? this.renderCommandBar : undefined}
                    >
                        {this.isMember && <Tab id="mine" name={Resources.MineTab} url={this.tabUrls["mine"]} />}
                        <Tab id="active" name={Resources.ActiveTab} url={this.tabUrls["active"]} />
                        <Tab id="completed" name={Resources.CompletedTab} url={this.tabUrls["completed"]} />
                        <Tab id="abandoned" name={Resources.AbandonedTab} url={this.tabUrls["abandoned"]} />
                    </TabBar>
                    <Observer filterVisible={this.filterVisible}>
                        {(props: { filterVisible?: boolean }) => {
                            if (!props.filterVisible) {
                                return null;
                            }

                            const assignedToFilterValue = this.filter.getFilterItemValue<IIdentity>("assignedTo");
                            const createdByFilterValue = this.filter.getFilterItemValue<IIdentity>("createdBy");

                            let initialAssignedTo: ObservableValue<IIdentity> | IIdentity = this.initialAssignedTo;
                            let initialAssignedToText: ObservableValue<string> | string = this.initialAssignedToText;

                            if (assignedToFilterValue && assignedToFilterValue.displayName) {
                                initialAssignedTo = assignedToFilterValue;
                                initialAssignedToText = assignedToFilterValue.displayName;
                            }

                            let initialCreatedBy: ObservableValue<IIdentity> | IIdentity = this.initialCreatedBy;
                            let initialCreatedByText: ObservableValue<string> | string = this.initialCreatedByText;

                            if (createdByFilterValue && createdByFilterValue.displayName) {
                                initialCreatedBy = createdByFilterValue;
                                initialCreatedByText = createdByFilterValue.displayName;
                            }

                            return (
                                <div className="page-content-left page-content-right page-content-top">
                                    <FilterBar
                                        className="repos-pr-listing-filterbar"
                                        filter={this.filter}
                                        onDismissClicked={this.onFilterDismissClicked}
                                    >
                                        <TextField
                                            className="bolt-text-filterbaritem flex-grow"
                                            containerClassName="flex-grow"
                                            focusTreatment={TextFieldFocusTreatmentBehavior.none}
                                            inputClassName="bolt-text-filterbaritem-input"
                                            onChange={this.onChangePullRequestId}
                                            onKeyDown={this.onKeyDownPullRequestId}
                                            placeholder={Resources.PullRequestIdPlaceholder}
                                            value={this.pullRequestId}
                                        />
                                        <IdentityPickerDropdownFilterBarItem
                                            editPlaceholder={Resources.CreatedBy}
                                            filterItemKey="createdBy"
                                            initialTextValue={initialCreatedByText}
                                            initialValue={initialCreatedBy}
                                            pickerProvider={this.pickerProvider}
                                            placeholder={Resources.CreatedBy}
                                        />
                                        <IdentityPickerDropdownFilterBarItem
                                            editPlaceholder={Resources.AssignedTo}
                                            filterItemKey="assignedTo"
                                            initialTextValue={initialAssignedToText}
                                            initialValue={initialAssignedTo}
                                            pickerProvider={this.pickerProvider}
                                            placeholder={Resources.AssignedTo}
                                        />
                                        <VersionDropdownFilterBarItem
                                            filterItemKey="targetRefName"
                                            placeholder={Resources.TargetBranch}
                                            initiallySelectedItem={this.filter.getFilterItemValue("targetRefName")}
                                        />
                                    </FilterBar>
                                </div>
                            );
                        }}
                    </Observer>
                    <PullRequestListingPageTab filter={this.filter} onLoaded={this.onLoaded} selectedTabId={this.selectedTabId} />
                </Page>
            </Surface>
        );
    }

    private createFilterHistoryFieldForIdentity(filterKey: string): IPageHistoryObjectFilterField<IIdentity> {
        return {
            dataType: "object",
            filterKey: filterKey,
            historyBehavior: HistoryBehavior.replace,
            mappingFunction: data => {
                if (data && data.localId) {
                    return data.localId;
                }
                return "";
            },
            parsingFunction: navValue => {
                if (navValue) {
                    const skeletonIdentity = { localId: navValue } as IIdentity;
                    if (filterKey === "assignedTo") {
                        this.initialAssignedTo = new ObservableValue<IIdentity>(skeletonIdentity);
                        this.initialAssignedToText = new ObservableValue<string>("");
                    } else {
                        this.initialCreatedBy = new ObservableValue<IIdentity>(skeletonIdentity);
                        this.initialCreatedByText = new ObservableValue<string>("");
                    }

                    (this.pickerProvider.getEntityFromUniqueAttribute(navValue) as PromiseLike<IIdentity>).then(identity => {
                        if (filterKey === "assignedTo") {
                            this.initialAssignedTo.value = identity;
                            this.initialAssignedToText.value = identity.displayName!;
                        } else {
                            this.initialCreatedBy.value = identity;
                            this.initialCreatedByText.value = identity.displayName!;
                        }
                    });
                    return skeletonIdentity;
                }
                return (undefined as unknown) as IIdentity;
            }
        };
    }

    private filterIdentities = (entityList: IIdentity[]): IIdentity[] => {
        // Only materialize identities when the FF is On.Else we filter identities based on the localId
        // and need not care about materialization.

        if (!this.isMaterializePullRequestIdentitiesFFEnabled) {
            return entityList && entityList.filter(entity => !!entity.localId);
        }
        return entityList;
    }

    private getCommandBarItems(tabId: string, screenSize: ScreenSize): IHeaderCommandBarItem[] {
        if (tabId === "mine" && this.isMember) {
            return [
                ...this.commandBarItems,
                {
                    id: "customize-queries",
                    important: screenSize >= ScreenSize.medium,
                    onActivate: () => {
                        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
                        contributionService.executeCommandEx("ms.vss-code-web.pr-listing-page-actions-command", {
                            dependencies: ["ms.vss-code-web.pr-listing-page-actions-content"],
                            serviceName: "IPullRequestListingPageActionsService",
                            methodName: "showCustomizeQueriesPanel"
                        });
                    },
                    text: Resources.CustomizeQueries
                }
            ];
        }

        return this.commandBarItems;
    }

    private onActivateCreatePullRequest = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        onClickFPS(this.context.pageContext, this.commandBarItems[0].href!, true, event);
    };

    private onChangePullRequestId = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        this.pullRequestId.value = value;
    };

    private onFilterDismissClicked = () => {
        this.filterVisible.value = false;
        this.pullRequestId.value = "";
        if (this.commandBarRef.current) {
            this.commandBarRef.current.focus({ commandBarItemId: "filter" });
        }
    };

    private onKeyDownPullRequestId = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === KeyCode.enter) {
            const pullRequestListingService = this.context.pageContext.getService<IPullRequestListingService>("IPullRequestListingService");
            pullRequestListingService.navigateToPullRequest(this.pullRequestId.value);
            event.preventDefault();
        }
    };

    private onLoaded = () => {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive("Repos", "prs-list-page");
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;

        if (newTabId !== "mine" && this.filterVisible.value === undefined) {
            this.filterVisible.value = true;
        }
    };

    private renderCommandBar = () => {
        return <HeaderCommandBarWithFilter filter={this.filter} filterToggled={this.filterVisible} items={[]} ref={this.commandBarRef} />;
    };
}

Components.add("vss-code-web-pr-listing-page", PullRequestListingPage);
